import React, { useEffect, useRef } from 'react';
import { useLogto } from '@logto/react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { REDIRECT_URI, RETURN_URL_KEY } from '@/logto';

// Bounces the browser to Logto's hosted sign-in page. Logto owns the login,
// registration and forgot-password UI, so there is nothing to render here
// beyond a spinner while the redirect happens.
//
// `firstScreen` picks which of Logto's screens to land on.
const AuthRedirect = ({ firstScreen }) => {
  const { isAuthenticated, isLoading, signIn } = useLogto();
  const [searchParams] = useSearchParams();
  const startedRef = useRef(false);

  useEffect(() => {
    if (isLoading || isAuthenticated || startedRef.current) {
      return;
    }
    // Guard against StrictMode's double-invoked effects kicking off two redirects.
    startedRef.current = true;

    // ProtectedRoute appends ?returnUrl=... when it bounces an unauthenticated
    // visitor. The OIDC round-trip would lose it, so park it until the callback.
    const returnUrl = searchParams.get('returnUrl');
    if (returnUrl) {
      sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    }

    signIn(firstScreen ? { redirectUri: REDIRECT_URI, firstScreen } : REDIRECT_URI);
  }, [isLoading, isAuthenticated, signIn, firstScreen, searchParams]);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-gray-600">Redirecting to sign-in…</p>
      </div>
    </div>
  );
};

export default AuthRedirect;
